import { DrunkDrink, convertVolumeToMl } from "./slices/alcoholSlice";
import { User, genders } from "./slices/userSlice";

const calculateRemainingAlcoholWeight = ({
  DrunkDrinks,
  user,
  timestamp = new Date().getTime(),
}: {
  DrunkDrinks: DrunkDrink[];
  user: User;
  timestamp?: number;
}) => {
  const drunkDrinksWithMl: (DrunkDrink & { ml: number })[] = DrunkDrinks.filter(
    (drunkDrink) => drunkDrink.date <= timestamp
  ).map((drunkDrink) => {
    return {
      ...drunkDrink,
      ml: convertVolumeToMl(
        drunkDrink.container.volume,
        drunkDrink.container.units
      ),
    };
  });

  drunkDrinksWithMl.sort((a, b) => a.date - b.date);

  var remainingAlcoholWeight = 0;

  drunkDrinksWithMl.forEach((DrunkDrink, index) => {
    const timeTillNextDrink =
      index + 1 < drunkDrinksWithMl.length
        ? drunkDrinksWithMl[index + 1].date - DrunkDrink.date
        : timestamp - DrunkDrink.date;

    const alcoholWeight = calculateDrunkDrinkAlcoholWeight({
      alcoholVolume: DrunkDrink.drink.alcoholVolume,
      volumeInMl: DrunkDrink.ml,
    });

    remainingAlcoholWeight = Math.max(
      alcoholWeight +
        remainingAlcoholWeight -
        calculateSoberSpeed({ timeInMs: timeTillNextDrink, user }),
      0
    );
  });

  return remainingAlcoholWeight;
};

export const calculatePermille = ({
  DrunkDrinks,
  user,
  timestamp = new Date().getTime(),
}: {
  DrunkDrinks: DrunkDrink[];
  user: User;
  timestamp?: number;
}) => {
  const remainingAlcoholWeight = calculateRemainingAlcoholWeight({
    DrunkDrinks,
    user,
    timestamp,
  });

  let permille = calculatePermilleFromAlcoholWeight({
    alcoholWeight: remainingAlcoholWeight,
    user,
  });

  return Math.round(permille * 100) / 100;
};

export const calculateMaxPermille = ({
  DrunkDrinks,
  user,
}: {
  DrunkDrinks: DrunkDrink[];
  user: User;
}) => {
  let maxPermille = 0;

  DrunkDrinks.forEach((DrunkDrink) => {
    const permille = calculatePermille({
      DrunkDrinks: DrunkDrinks.filter(
        (DrunkDrinks) => DrunkDrinks.date <= DrunkDrink.date
      ),
      user,
      timestamp: DrunkDrink.date,
    });

    if (permille > maxPermille) {
      maxPermille = permille;
    }
  });

  return maxPermille;
};

export const calculateDrunkDrinkPermille = ({
  alcoholVolume,
  volumeInMl,
  timeInMs,
  user,
}: {
  alcoholVolume: number;
  volumeInMl: number;
  timeInMs: number;
  user: User;
}): number => {
  let alcoholWeight = calculateDrunkDrinkAlcoholWeight({
    alcoholVolume,
    volumeInMl,
  });

  let soberSpeed = calculateSoberSpeed({ timeInMs, user });

  let alcoholPermille =
    Math.round(
      calculatePermilleFromAlcoholWeight({
        alcoholWeight: alcoholWeight - soberSpeed,
        user,
      }) * 100
    ) / 100;

  if (alcoholPermille < 0) alcoholPermille = 0;

  return alcoholPermille;
};

const calculatePermilleFromAlcoholWeight = ({
  alcoholWeight,
  user,
}: {
  alcoholWeight: number;
  user: User;
}) => {
  const weight = user.weight || 60;
  const permilleCalculationConstant =
    user.gender === genders.Female ? 0.6 : 0.7;

  return alcoholWeight / (weight * permilleCalculationConstant);
};

const calculateSoberSpeed = ({
  timeInMs,
  user,
}: {
  timeInMs: number;
  user: User;
}) => {
  const weight = user.weight || 60;
  const soberSpeedConstant = user.gender === genders.Female ? 0.085 : 0.1;

  return (weight * soberSpeedConstant * timeInMs) / 1000 / 60 / 60;
};

export const calculateTimeToSober = ({
  DrunkDrinks,
  user,
}: {
  DrunkDrinks: DrunkDrink[];
  user: User;
}) => {
  const remainingAlcoholWeight = calculateRemainingAlcoholWeight({
    DrunkDrinks,
    user,
    timestamp: new Date().getTime(),
  });

  const weight = user.weight || 60;
  const soberSpeedConstant = user.gender === genders.Female ? 0.085 : 0.1;

  if (remainingAlcoholWeight <= 0) return 0;

  return (3600000 * remainingAlcoholWeight) / (weight * soberSpeedConstant);
};

const calculateDrunkDrinkAlcoholWeight = ({
  alcoholVolume,
  volumeInMl,
}: {
  alcoholVolume: number;
  volumeInMl: number;
}) => {
  return (volumeInMl * alcoholVolume * 0.8) / 100;
};
